import React from 'react'
import { api, type Plan } from '../../api/client'
import { WEEKDAYS, formatDay, minutesToTime } from '../../catalog/format'
import DayEditor from '../../components/DayEditor'
import { dropDay } from '../../plan/dropDay'
import { computeForecast, type GoalNumbers, type Stretch } from '../../plan/forecast'
import {
  COPY_WORKING_HOURS,
  COPY_WORKING_HOURS_ASK,
  COPY_WORKING_HOURS_KEEP,
  COPY_WORKING_HOURS_REPLACE,
  DREAM_WEEK_ABOUT,
  DREAM_WEEK_ABOUT_TITLE,
  FOOTER_NOT_SAVED,
  FOOTER_PLAN_ONLY,
  FOOTER_UNDO,
  GO_LIVE_ASK,
  GO_LIVE_LEAD,
  GO_LIVE_NOT_YET,
  GO_LIVE_YES,
  LET_CLIENTS_BOOK,
  SAVE_AS_PLAN,
  TAP_A_DAY,
  WEEK_LEAD,
  dayLabel,
  dropDayLine,
  goLiveWas,
  weekShape,
  weekStatus,
  weekSummary,
} from '../../plan/wording'
import { Icon } from '../../ui/Icon'
import { InfoTip } from '../../ui/InfoTip'
import { toDraftWeek } from '../settings/hoursForm'
import { saveErrorMessage } from '../settings/toast'
import PlanResult from './PlanResult'
import type { PlanWeek } from './usePlanWeek'
import WeekStrip from './WeekStrip'

interface Props {
  plan: Plan
  goal: GoalNumbers
  week: PlanWeek
  selected: number | null
  onSelect: (day: number | null) => void
  open: boolean
  onToggle: () => void
}

interface DayChange {
  day: number
  next: Stretch[]
  now: Stretch[]
}

const sameStretches = (a: Stretch[], b: Stretch[]) =>
  a.length === b.length && a.every((s, i) => s.start_minute === b[i].start_minute && s.end_minute === b[i].end_minute)

const byStart = (a: Stretch, b: Stretch) => a.start_minute - b.start_minute

// DreamWeekCard is the week she'd like to work and what it would take home,
// in one card. Its header opens and closes it: the title with a tag (not
// saved, plan only, or her working hours), then the week's shape and
// take-home on one line, with the ⓘ beside it. Open, it holds the week to
// edit a day at a time, then the result against her goal. A footer, sticky
// on a phone while it's needed, holds the one filled button (letting clients
// book these hours, which asks first and lists each day that changes) and
// the quiet save as a plan.
export default function DreamWeekCard({ plan, goal, week, selected, onSelect, open, onToggle }: Props) {
  const busy = week.busy !== null
  // Copying her working hours in replaces the whole week, so once she has
  // filled in any day it asks first. It's hidden when the week already is
  // her working hours.
  const [askCopy, setAskCopy] = React.useState(false)
  const canCopy = !(plan.live && !week.unsaved)
  const copyHours = () => {
    if (week.week.some((day) => day.length > 0)) {
      setAskCopy(true)
    } else {
      void week.startFromCurrentHours()
    }
  }

  // Letting clients book asks first, with each day that would change. With
  // nothing changing it goes straight ahead.
  const [changes, setChanges] = React.useState<DayChange[] | null>(null)
  const [askError, setAskError] = React.useState('')
  // The question appears below the week's result, which on a phone is off
  // screen when she taps the sticky footer, so it brings itself into view.
  const askRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (changes) askRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  }, [changes])
  const askGoLive = async () => {
    setAskError('')
    try {
      const current = await api.getWorkingHours()
      const found = WEEKDAYS.map((_, day) => ({
        day,
        next: week.liveWindows.filter((s) => s.weekday === day).sort(byStart),
        now: current.filter((s) => s.weekday === day).sort(byStart),
      })).filter((c) => !sameStretches(c.next, c.now))
      if (found.length === 0) {
        void week.useAsHours(false)
      } else {
        setChanges(found)
      }
    } catch (e) {
      setAskError(saveErrorMessage(e))
    }
  }
  const goLive = () => {
    setChanges(null)
    void week.useAsHours(false)
  }

  // Over the goal, the day she could drop and still reach it. Trying it
  // empties that day in the draft; Undo puts it back.
  const drop = dropDay(week.liveWindows, plan.inputs, goal)
  const tip = drop
    ? {
        text: dropDayLine(WEEKDAYS[drop.day], drop.take_home_pence),
        onTry: () => week.edit(week.week.map((stretches, day) => (day === drop.day ? [] : stretches))),
      }
    : null

  const status = weekStatus(week.unsaved, plan.live, plan.windows.length > 0)
  // The footer shows while there's a change to save, or a saved plan that
  // clients can't book yet.
  const footer = week.unsaved ? FOOTER_NOT_SAVED : !plan.live && plan.windows.length > 0 ? FOOTER_PLAN_ONLY : null

  return (
    <section className="goals-card dream-week">
      <div className="dream-week__head">
        <button type="button" className="dream-week__summary" aria-expanded={open} onClick={onToggle}>
          <span className="dream-week__text">
            <span className="dream-week__title-row">
              <span className="goals-card-title">Dream Week</span>
              {status && <span className={`tag tag--${status.tone}`}>{status.text}</span>}
            </span>
            <span className="dream-week__meta">{weekSummary(week.forecast)}</span>
          </span>
          <Icon name="chevron-down" size={18} className={`dream-week__chevron${open ? ' open' : ''}`} />
        </button>
        <InfoTip title={DREAM_WEEK_ABOUT_TITLE} lines={DREAM_WEEK_ABOUT} className="dream-week__info" />
      </div>

      {open && (
        <div className="dream-week__body">
          <WeekStrip week={week.liveWindows} selected={selected} onSelect={(day) => onSelect(day === selected ? null : day)} />
          {week.errors.week && (
            <p className="field-error" role="alert">
              {week.errors.week}
            </p>
          )}
          {selected !== null ? (
            <div className="goals-day">
              <DayEditor week={week.week} day={selected} onChange={week.edit} errors={week.errors} />
              <button type="button" className="btn btn-quiet btn-compact" onClick={() => onSelect(null)}>
                Done
              </button>
            </div>
          ) : askCopy ? (
            <div className="dream-week__ask" role="alertdialog" aria-label={COPY_WORKING_HOURS_ASK}>
              <p className="dream-week__ask-title">{COPY_WORKING_HOURS_ASK}</p>
              <div className="dream-week__ask-actions">
                <button type="button" className="btn btn-quiet btn-compact" onClick={() => setAskCopy(false)}>
                  {COPY_WORKING_HOURS_KEEP}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-compact"
                  onClick={() => {
                    setAskCopy(false)
                    void week.startFromCurrentHours()
                  }}
                >
                  {COPY_WORKING_HOURS_REPLACE}
                </button>
              </div>
            </div>
          ) : (
            <div className="dream-week__tools">
              <p className="goals-muted">{TAP_A_DAY}</p>
              {canCopy && (
                <button type="button" className="btn btn-quiet btn-compact" onClick={copyHours} disabled={busy}>
                  {COPY_WORKING_HOURS}
                </button>
              )}
            </div>
          )}

          {week.error && (
            <p className="field-error" role="alert">
              {week.error}
            </p>
          )}

          <PlanResult forecast={week.forecast} goal={goal} tip={tip} />

          {week.outside && (
            <div className="goals-outside" role="alertdialog" aria-label="Appointments outside the new working hours">
              <p>
                {week.outside.length === 1
                  ? 'This appointment falls outside the new working hours. It’ll stay exactly where it is.'
                  : `These ${week.outside.length} appointments fall outside the new working hours. They’ll stay exactly where they are.`}
              </p>
              <ul>
                {week.outside.map((a) => (
                  <li key={a.id}>
                    {formatDay(a.date)}, {minutesToTime(a.start_minute)}
                    {a.client ? ` · ${a.client.first_name}` : ''}
                  </li>
                ))}
              </ul>
              <div className="goals-actions">
                <button type="button" className="btn btn-secondary btn-compact" onClick={week.dismissOutside}>
                  Don’t change my hours
                </button>
                <button type="button" className="btn btn-primary btn-compact" onClick={() => week.useAsHours(true)} disabled={busy}>
                  Use them anyway
                </button>
              </div>
            </div>
          )}

          {changes && (
            <div className="dream-week__ask" role="alertdialog" aria-label={GO_LIVE_ASK} ref={askRef}>
              <p className="dream-week__ask-title">{GO_LIVE_ASK}</p>
              <p className="goals-muted">{GO_LIVE_LEAD}</p>
              <ul className="dream-week__changes">
                {changes.map((c) => (
                  <li key={c.day}>
                    <span>{dayLabel(WEEKDAYS[c.day], c.next)}</span>
                    <span className="goals-muted">{goLiveWas(c.now)}</span>
                  </li>
                ))}
              </ul>
              <div className="dream-week__ask-actions">
                <button type="button" className="btn btn-quiet btn-compact" onClick={() => setChanges(null)}>
                  {GO_LIVE_NOT_YET}
                </button>
                <button type="button" className="btn btn-primary btn-compact" onClick={goLive} disabled={busy}>
                  {GO_LIVE_YES}
                </button>
              </div>
            </div>
          )}
          {askError && (
            <p className="field-error" role="alert">
              {askError}
            </p>
          )}

          {footer && !changes && !week.outside && (
            <div className="dream-week__footer">
              <span className="dream-week__footer-note">
                {footer}
                {/* Undo puts back the saved week; a first week has nothing to go back to. */}
                {week.unsaved && plan.goal !== null && (
                  <button type="button" className="btn btn-quiet btn-compact dream-week__undo" onClick={() => week.edit(toDraftWeek(plan.windows))} disabled={busy}>
                    {FOOTER_UNDO}
                  </button>
                )}
              </span>
              {week.unsaved && (
                <button type="button" className="btn btn-quiet btn-compact dream-week__save" onClick={week.keepAsPlan} disabled={busy}>
                  {week.busy === 'save' ? 'Saving…' : SAVE_AS_PLAN}
                </button>
              )}
              <button type="button" className="btn btn-primary btn-compact dream-week__go-live" onClick={askGoLive} disabled={busy}>
                {week.busy === 'apply' ? 'Applying…' : LET_CLIENTS_BOOK}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

const NO_GOAL: GoalNumbers = { take_home_target_pence: 0, monthly_costs_pence: 0, holiday_weeks_per_year: 0 }

// DreamWeekPreview is the Dream Week card as Free sees it under the Goals
// lock (2026-10-24): the week from GET /v1/plan (her working hours, or a plan
// she saved on Pro) drawn on the week strip with its days and hours, the
// pounds left as "£—", and nothing to tap. No goal is needed.
export function DreamWeekPreview({ plan }: { plan: Pick<Plan, 'windows' | 'inputs'> }) {
  return (
    <section className="goals-card dream-week">
      <div className="dream-week__head">
        <div className="dream-week__summary">
          <span className="dream-week__text">
            <span className="dream-week__title-row">
              <span className="goals-card-title">Dream Week</span>
            </span>
            <span className="dream-week__meta">{weekShape(computeForecast(plan.windows, plan.inputs, NO_GOAL))}</span>
          </span>
        </div>
      </div>
      <div className="dream-week__body">
        <WeekStrip week={plan.windows} />
        <div className="plan-result">
          <section className="progress-section">
            <p className="progress-lead">{WEEK_LEAD}</p>
            <div className="progress-row">
              <span className="progress-amount">
                £—<span className="progress-amount__unit"> a month</span>
              </span>
            </div>
            <div className="progress-bar" />
          </section>
        </div>
      </div>
    </section>
  )
}
